import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TestimonialsSection = () => {
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  const testimonials = [
    {
      id: 1,
      name: "Dr. Sarah Chen",
      title: "Dean of Computer Science",
      institution: "Stanford University",
      institutionType: "Academic",
      avatar: "https://randomuser.me/api/portraits/women/44.jpg",
      quote: `Dream Coders Club transformed our AI curriculum completely. We saw a 40% increase in student engagement and 95% job placement rate within 6 months of graduation. The platform's hands-on approach and industry connections are unmatched.`,
      metrics: {
        engagement: "40% increase",
        placement: "95% job placement",
        timeline: "6 months"
      },
      results: [
        "Curriculum modernization in 8 weeks",
        "Faculty training for 25 professors",
        "Student satisfaction score: 4.8/5",
        "Industry partnership with 15 companies"
      ]
    },
    {
      id: 2,
      name: "Michael Rodriguez",
      title: "Chief Learning Officer",
      institution: "Microsoft Corporation",
      institutionType: "Corporate",
      avatar: "https://randomuser.me/api/portraits/men/32.jpg",
      quote: `Our workforce transformation initiative exceeded all expectations. Dream Coders Club's enterprise solution helped us upskill 2,000+ employees in AI technologies, resulting in 60% faster project delivery and significant cost savings.`,
      metrics: {
        employees: "2,000+ trained",
        delivery: "60% faster",
        savings: "$2.5M annually"
      },
      results: [
        "Enterprise-wide AI adoption",
        "Reduced external consulting costs by 70%",
        "Internal innovation projects increased 3x",
        "Employee retention improved by 25%"
      ]
    },
    {
      id: 3,
      name: "Colonel James Patterson",
      title: "Director of Technology Training",
      institution: "Department of Defense",
      institutionType: "Government",
      avatar: "https://randomuser.me/api/portraits/men/55.jpg",
      quote: `The security-compliant platform and specialized curriculum met our stringent requirements perfectly. We successfully trained 500+ personnel in critical AI applications while maintaining the highest security standards.`,
      metrics: {
        personnel: "500+ trained",
        compliance: "100% security",
        efficiency: "50% improvement"
      },
      results: [
        "Security clearance-compatible training",
        "Operational efficiency improved 50%",
        "Multi-agency collaboration enhanced",
        "Critical mission capabilities developed"
      ]
    },
    {
      id: 4,
      name: "Dr. Emily Watson",
      title: "VP of Innovation",
      institution: "Johns Hopkins University",
      institutionType: "Academic",
      avatar: "https://randomuser.me/api/portraits/women/28.jpg",
      quote: `The research collaboration opportunities and advanced lab configurations have elevated our graduate programs. Our students are now publishing in top-tier conferences and securing positions at leading tech companies.`,
      metrics: {
        publications: "3x increase",
        placements: "98% success rate",
        research: "15 active projects"
      },
      results: [
        "Graduate program ranking improved to Top 5",
        "Research funding increased by $5M",
        "Industry partnerships with FAANG companies",
        "Student startup success rate: 40%"
      ]
    }
  ];

  const institutionTypes = [
    { type: 'Academic', icon: 'GraduationCap', color: 'text-accent' },
    { type: 'Corporate', icon: 'Building2', color: 'text-success' },
    { type: 'Government', icon: 'Shield', color: 'text-warning' }
  ];

  const currentTestimonial = testimonials[activeTestimonial];

  return (
    <section className="py-20 bg-gradient-to-br from-background to-primary">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-foreground mb-4">
            Trusted by
            <span className="gradient-text"> Industry Leaders</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            See how leading institutions are transforming their organizations with our AI/Robotics education solutions
          </p>
        </div>

        {/* Institution Type Filter */}
        <div className="flex justify-center mb-12">
          <div className="flex space-x-2 p-2 bg-card rounded-lg border border-border">
            {institutionTypes.map((type, index) => (
              <button
                key={type.type}
                onClick={() => setActiveTestimonial(testimonials.findIndex(t => t.institutionType === type.type))}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all duration-300 ${
                  currentTestimonial.institutionType === type.type
                    ? 'bg-accent text-white shadow-glow'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                }`}
              >
                <Icon name={type.icon} size={16} />
                <span className="text-sm font-medium">{type.type}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Column - Testimonial */}
          <div className="space-y-8">
            <div className="holographic-card p-8 bg-card/50 backdrop-blur-md">
              {/* Quote */}
              <div className="mb-6">
                <Icon name="Quote" size={32} className="text-accent mb-4" />
                <blockquote className="text-lg text-foreground leading-relaxed">
                  "{currentTestimonial.quote}"
                </blockquote>
              </div>

              {/* Author */}
              <div className="flex items-center space-x-4 pt-6 border-t border-border">
                <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-accent/20">
                  <img 
                    src={currentTestimonial.avatar} 
                    alt={currentTestimonial.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <div className="font-semibold text-foreground text-lg">{currentTestimonial.name}</div>
                  <div className="text-muted-foreground">{currentTestimonial.title}</div>
                  <div className="text-accent font-medium">{currentTestimonial.institution}</div>
                </div>
              </div>
            </div>

            {/* Navigation */}
            <div className="flex justify-center space-x-2">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setActiveTestimonial(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    index === activeTestimonial
                      ? 'bg-accent shadow-glow'
                      : 'bg-muted hover:bg-accent/50'
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Right Column - Metrics & Results */}
          <div className="space-y-6">
            {/* Key Metrics */}
            <div className="holographic-card p-6 bg-card/50 backdrop-blur-md">
              <h3 className="text-xl font-bold text-foreground mb-6">Key Results</h3>
              <div className="grid grid-cols-1 gap-4">
                {Object.entries(currentTestimonial.metrics).map(([key, value]) => (
                  <div key={key} className="flex items-center justify-between p-4 bg-background/50 rounded-lg">
                    <span className="text-muted-foreground capitalize">{key.replace(/([A-Z])/g, ' $1')}</span>
                    <span className="text-lg font-bold text-accent">{value}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Detailed Results */}
            <div className="holographic-card p-6 bg-card/50 backdrop-blur-md">
              <h3 className="text-xl font-bold text-foreground mb-6">Implementation Outcomes</h3>
              <div className="space-y-3">
                {currentTestimonial.results.map((result, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <Icon name="CheckCircle" size={16} className="text-success mt-1 flex-shrink-0" />
                    <span className="text-muted-foreground">{result}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA */}
            <div className="space-y-4">
              <Button
                variant="default"
                size="lg"
                fullWidth
                className="morphing-cta text-white font-semibold"
                iconName="MessageCircle"
                iconPosition="left"
              >
                Speak with {currentTestimonial.name}
              </Button>
              
              <Button
                variant="outline"
                size="lg"
                fullWidth
                iconName="FileText"
                iconPosition="left"
              >
                Read Full Case Study
              </Button>
            </div>
          </div>
        </div>

        {/* Trust Indicators */}
        <div className="mt-20 text-center">
          <div className="text-sm text-muted-foreground mb-6">Trusted by 500+ institutions worldwide</div>
          <div className="flex flex-wrap justify-center items-center gap-8 opacity-60">
            <div className="px-6 py-3 bg-card rounded-lg border border-border">
              <span className="font-semibold text-foreground">Harvard</span>
            </div>
            <div className="px-6 py-3 bg-card rounded-lg border border-border">
              <span className="font-semibold text-foreground">MIT</span>
            </div>
            <div className="px-6 py-3 bg-card rounded-lg border border-border">
              <span className="font-semibold text-foreground">Google</span>
            </div>
            <div className="px-6 py-3 bg-card rounded-lg border border-border">
              <span className="font-semibold text-foreground">Microsoft</span>
            </div>
            <div className="px-6 py-3 bg-card rounded-lg border border-border">
              <span className="font-semibold text-foreground">Amazon</span>
            </div>
            <div className="px-6 py-3 bg-card rounded-lg border border-border">
              <span className="font-semibold text-foreground">IBM</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;